/**
 * 列表行的左滑操作（iOS swipe actions）。
 *
 * 结构约定（组件按这个渲染，hook 只负责动）：
 *   <li>                        外层，overflow hidden
 *     <div data-swipe-actions>  绝对定位在右侧的操作区
 *     <div data-swipe-content>  真正被平移的内容
 *
 * 四个手感细节：
 *   1. **轴判断**：横移要明显大于竖移才接管手势，否则用户想上下滚页面会被判成滑动
 *   2. **超滑阻尼**：滑过操作区宽度后进入阻尼区，滑到底直接触发主操作
 *   3. **按速度吸附**：轻甩一下也能打开，不必拖满整个宽度
 *   4. **互斥**：同一时刻只允许一行打开，打开新的自动收起旧的
 *
 * 用 `touch-action: pan-y` 声明式地把横向手势留给自己，
 * 不调 preventDefault，于是也不需要非被动监听器。
 */

import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

import { MOTION, gsap, prefersReducedMotion } from './gsap';

/** 模块级：当前处于打开状态的那一行 */
let closeCurrent: (() => void) | null = null;

const AXIS_LOCK = 8;
/** 横移要比竖移大这么多倍才认定为滑动，避免斜着滚页面时误触发 */
const AXIS_RATIO = 1.3;
/** 右滑（用于收起）的阻尼 */
const RIGHT_RESISTANCE = 0.5;
/** 超滑阻尼 */
const OVERDRAG_RESISTANCE = 0.35;
/** 轻甩判定（px/ms） */
const OPEN_VELOCITY = 0.4;
/** 滑过操作区宽度的多少倍算"滑到底" */
const FULL_SWIPE_FACTOR = 2.2;

export interface SwipeRevealOptions {
	/** 操作区宽度（px），必须和组件里写死的宽度一致 */
	revealWidth?: number;
	/** 一路滑到底时直接触发主操作 */
	onFullSwipe?: () => void;
	disabled?: boolean;
}

export interface SwipeRevealControls {
	rowRef: RefObject<HTMLLIElement>;
	isOpen: boolean;
	close: () => void;
}

export function useSwipeReveal({
	revealWidth = 92,
	onFullSwipe,
	disabled = false,
}: SwipeRevealOptions = {}): SwipeRevealControls {
	const rowRef = useRef<HTMLLIElement | null>(null);
	const [isOpen, setIsOpen] = useState(false);
	const openRef = useRef(false);
	const closeRef = useRef<() => void>(() => {});
	const fullSwipeRef = useRef(onFullSwipe);
	const reduced = prefersReducedMotion();

	fullSwipeRef.current = onFullSwipe;

	useEffect(() => {
		const row = rowRef.current;
		if (!row || disabled) return;

		const content = row.querySelector<HTMLElement>('[data-swipe-content]');
		const actions = row.querySelector<HTMLElement>('[data-swipe-actions]');
		if (!content || !actions) return;

		// 减弱动态效果：不做手势，操作区常驻显示（功能不丢，只是没有了位移动画）
		if (reduced) {
			gsap.set(actions, { opacity: 0 });
			closeRef.current = () => {};
			return;
		}

		const actionsSetter = gsap.quickSetter(actions, 'opacity') as (value: number) => void;

		let tracking = false;
		let active = false;
		let startX = 0;
		let startY = 0;
		let startOffset = 0;
		let offset = 0;
		let lastX = 0;
		let lastTime = 0;
		let velocity = 0;
		/** 上一次拖拽结束的时间戳。用来吃掉拖拽尾随的那次 click。 */
		let lastDragEndAt = 0;

		const applyOffset = (value: number): void => {
			offset = value;
			gsap.set(content, { x: value });
			actionsSetter(Math.min(1, Math.abs(value) / revealWidth));
		};

		const close = (): void => {
			openRef.current = false;
			setIsOpen(false);
			if (closeCurrent === close) closeCurrent = null;
			gsap.to(content, {
				x: 0,
				duration: 0.36,
				ease: MOTION.ease.out,
				overwrite: 'auto',
				onComplete: () => gsap.set(content, { clearProps: 'transform' }),
			});
			gsap.to(actions, { opacity: 0, duration: 0.2, ease: 'power2.out' });
		};

		const open = (): void => {
			if (closeCurrent && closeCurrent !== close) closeCurrent();
			closeCurrent = close;
			openRef.current = true;
			setIsOpen(true);
			gsap.to(content, {
				x: -revealWidth,
				duration: 0.42,
				ease: MOTION.ease.out,
				overwrite: 'auto',
			});
			gsap.to(actions, { opacity: 1, duration: 0.24, ease: 'power2.out' });
		};

		closeRef.current = close;

		const onPointerDown = (event: PointerEvent): void => {
			if (event.pointerType === 'mouse' && event.button !== 0) return;
			tracking = true;
			active = false;
			startX = event.clientX;
			startY = event.clientY;
			startOffset = openRef.current ? -revealWidth : 0;
			lastX = event.clientX;
			lastTime = performance.now();
			velocity = 0;
		};

		const onPointerMove = (event: PointerEvent): void => {
			if (!tracking) return;

			const dx = event.clientX - startX;
			const dy = event.clientY - startY;

			if (!active) {
				// 竖移更大 → 交还给页面滚动，本次手势不再接管
				if (Math.abs(dy) > Math.abs(dx) * AXIS_RATIO) {
					tracking = false;
					return;
				}
				if (Math.abs(dx) < AXIS_LOCK) return;
				active = true;
				gsap.killTweensOf(content);
			}

			const now = performance.now();
			const elapsed = now - lastTime;
			if (elapsed > 0) velocity = (event.clientX - lastX) / elapsed;
			lastX = event.clientX;
			lastTime = now;

			let next = startOffset + dx;
			if (next > 0) {
				next *= RIGHT_RESISTANCE;
			} else if (next < -revealWidth) {
				next = -revealWidth - (-revealWidth - next) * OVERDRAG_RESISTANCE;
			}

			applyOffset(next);
		};

	/**
	 * 点击的统一裁决点。挂在行的捕获阶段，早于内容自己的 onClick。
	 *
	 * 四种情况：
	 *   1. 刚拖拽完 → 吞掉这次 click。浏览器在 pointerup 后会补发一次，
	 *      不拦的话每滑一次都会顺带触发"点整行"（打开编辑面板），手势直接变灾难。
	 *   2. 已经滑开、点在操作区上 → **放行**，让「用完了」自己的 onClick 跑。
	 *      这条必须放在第 1 条之后：手指左滑收尾时正好停在操作区上，浏览器
	 *      补发的那次 click 的目标就是它 —— 不先吃掉拖拽尾随，每次滑开都会
	 *      误触发"用完了"。
	 *   3. 已经滑开、点在别处 → 只收起，并拦住内容自身的操作。
	 *      iOS 也是如此：滑开状态下点一下是"收起来"，不是"执行这一行"。
	 *   4. 其他 → 放行，交给行自己的 onClick。
	 */
	const onRowClickCapture = (event: MouseEvent): void => {
		const sinceDrag = performance.now() - lastDragEndAt;
		if (sinceDrag < 350) {
			event.stopPropagation();
			event.preventDefault();
			return;
		}
		if (openRef.current) {
			if (actions.contains(event.target as Node)) return;
			event.stopPropagation();
			event.preventDefault();
			close();
		}
	};

		const onPointerUp = (): void => {
			if (!tracking) return;
			const wasActive = active;
			tracking = false;
			active = false;
			if (!wasActive) return;

			lastDragEndAt = performance.now();

			const swipeDistance = -offset;

			// 一路滑到底：直接执行主操作，不停在打开状态
			if (swipeDistance > revealWidth * FULL_SWIPE_FACTOR && fullSwipeRef.current) {
				gsap.to(content, {
					x: -revealWidth * 2.6,
					duration: 0.2,
					ease: 'power2.in',
					onComplete: () => {
						close();
						fullSwipeRef.current?.();
					},
				});
				gsap.to(actions, { opacity: 0, duration: 0.16 });
				return;
			}

			const shouldOpen =
				swipeDistance > revealWidth * 0.45 || velocity < -OPEN_VELOCITY;

			if (shouldOpen) open();
			else close();
		};

		row.addEventListener('pointerdown', onPointerDown);
		window.addEventListener('pointermove', onPointerMove);
		window.addEventListener('pointerup', onPointerUp);
		window.addEventListener('pointercancel', onPointerUp);
		// 捕获阶段，确保早于内容自身的 onClick
		row.addEventListener('click', onRowClickCapture, { capture: true });

		return () => {
			row.removeEventListener('pointerdown', onPointerDown);
			window.removeEventListener('pointermove', onPointerMove);
			window.removeEventListener('pointerup', onPointerUp);
			window.removeEventListener('pointercancel', onPointerUp);
			row.removeEventListener('click', onRowClickCapture, { capture: true });
			if (closeCurrent === close) closeCurrent = null;
			gsap.killTweensOf(content);
			gsap.killTweensOf(actions);
		};
	}, [revealWidth, disabled, reduced]);

	const close = useCallback(() => closeRef.current(), []);

	return { rowRef, isOpen, close };
}
